/*
 * Copyright (c) CIRROM 2025.
 */

import React from "react";
import {
  isChunkLoadError,
  recoverFromChunkLoadError,
} from "../../utils/chunkRecovery";

interface LazyErrorBoundaryProps {
  children: React.ReactNode;
  /** Shown while the recovery reload is in flight - the route's own spinner. */
  fallback: React.ReactNode;
}

interface LazyErrorBoundaryState {
  reloading: boolean;
  error: unknown;
}

const INITIAL_STATE: LazyErrorBoundaryState = { reloading: false, error: null };

/**
 * Error boundary for lazily loaded routes.
 *
 * A chunk-load failure means a deploy replaced the contenthashed chunk the
 * cached index.html asks for: refresh once, silently, and the user lands back
 * on the same route. Anything else is rethrown, so genuine application errors
 * behave exactly as they did before this boundary existed.
 */
export class LazyErrorBoundary extends React.Component<
  LazyErrorBoundaryProps,
  LazyErrorBoundaryState
> {
  constructor(props: LazyErrorBoundaryProps) {
    super(props);
    this.state = INITIAL_STATE;
  }

  /**
   * Render phase: classification only, no side effects. StrictMode
   * double-invokes this in development, so it must stay pure - the reload
   * happens in componentDidCatch, which runs in the commit phase.
   */
  static getDerivedStateFromError(error: unknown): LazyErrorBoundaryState {
    return isChunkLoadError(error)
      ? { reloading: true, error: null }
      : { reloading: false, error };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo): void {
    if (!isChunkLoadError(error)) {
      console.error(
        "Unhandled error in lazy route",
        error,
        errorInfo.componentStack
      );
      return;
    }

    console.warn("Chunk load failure - refreshing", error);

    if (!recoverFromChunkLoadError()) {
      // Already refreshed once and the chunk is still missing: the cached
      // index.html is stale beyond what a reload fixes. Let the error through
      // rather than loop.
      this.setState({ reloading: false, error });
    }
  }

  render(): React.ReactNode {
    if (this.state.error) {
      throw this.state.error;
    }
    return this.state.reloading ? this.props.fallback : this.props.children;
  }
}
